import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Checkbox } from "../common/ControlBundle";
import ExpandDiv from "../common/ExpandDiv";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function KYILSkillsChecklist() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  //Get Client Info
  useEffect(() => {
    if (!clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await getCareTeams(clientid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setCareTeams(response);
    };

    loadClientInfo();
  }, [clientid]);

  useEffect(() => {
    if (!submissionid) {
      return;
    }

    const loadSubmission = async () => {
      const response = await getSubmission(submissionid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setData(response.Data);
      setFormsyDetails(response.FormsyDetails);

      if (response.Comments) {
        setComments(response.Comments);
      }

      if (response.Signatures) {
        setSignatures(response.Signatures);
      }
    };

    loadSubmission();
  }, [submissionid]);

  const [expandStatus, setExpandStatus] = useState(false);

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <button
                className="btn btn-primary mb-2"
                onClick={(e) => {
                  handleExpandStatusChange();
                }}
              >
                {expandStatus === true ? "Collapse All" : "Expand All"}
              </button>
              <ExpandDiv
                id="DailyLivingSkills"
                expandStatus={expandStatus}
                className="mt-2 ms-3"
                title="Daily Living Skills"
              >
                <h5>Telephone Skills</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="Calling"
                        label="Explain how to use a landline, cell phone"
                        checked={data.Calling["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="Texting"
                        label="Explain texting"
                        checked={data.Texting["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="Contacts"
                        label="Discuss importance of programming contact information in case of emergency, ie: Social Worker, Case Worker, next of kin, etc."
                        checked={data.Contacts["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="EmergencyCalling"
                        label="Tell how to use the telephone in an emergency"
                        checked={data.EmergencyCalling["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="Information"
                        label="Explain how to use information (411)"
                        checked={data.Information["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <h5>Internet</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="InternetSearch"
                        label="How to use a search engine, ie: Google"
                        checked={data.InternetSearch["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="InternetPrivacy"
                        label="Explain internet privacy and content"
                        checked={data.InternetPrivacy["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="InternetSafety"
                        label="Cyber Space Safety"
                        checked={data.InternetSafety["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="SocialNetworking"
                        label="Social Networking"
                        checked={data.SocialNetworking["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <h5>Transportation</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="PedestrianRules"
                        label="Demonstrate pedestrian safety rules"
                        checked={data.PedestrianRules["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="BicycleRules"
                        label="Explain bicycle road rules"
                        checked={data.BicycleRules["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <h5>Nutrition/Cooking</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="FoodPyramid"
                        label="Explain food pyramid"
                        checked={data.FoodPyramid["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="FoodStorage"
                        label="Explain proper care and storage of food"
                        checked={data.FoodStorage["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="KitchenSafety"
                        label="Discuss kitchen safety and how to recognize unsafe acts or conditions"
                        checked={data.KitchenSafety["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="KitchenEmergencies"
                        label="Discuss how to prepare for emergencies in the kitchen"
                        checked={data.KitchenEmergencies["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="RecipeTerms"
                        label="Identify terms and abbreviations used in recipes"
                        checked={data.RecipeTerms["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <h5>Home Maintenance</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="CleanKitchen"
                        label="Demonstrate kitchen cleaning and maintenance tasks"
                        checked={data.CleanKitchen["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="CleanBathroom"
                        label="Demonstrate bathroom cleaning and maintenance tasks"
                        checked={data.CleanBathroom["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="CleanBedroom"
                        label="Demonstrate bedroom cleaning and maintenance tasks"
                        checked={data.CleanBedroom["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="ChangeBedding"
                        label="Demonstrate changing bedding"
                        checked={data.ChangeBedding["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="CleanGeneral"
                        label="Demonstrate general cleaning of household"
                        checked={data.CleanGeneral["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="SelfCare"
                expandStatus={expandStatus}
                className="mt-2 ms-3"
                title="Self-Care"
              >
                <h5>Hygiene</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="Toothbrushing"
                        label="Demonstrate proper tooth brushing"
                        checked={data.Toothbrushing["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="ShowerRoutine"
                        label="Demonstrate proper shower routine"
                        checked={data.ShowerRoutine["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="BodyCare"
                        label="Demonstrate appropriate body care (i.e. deodorant, feminine hygiene products)"
                        checked={data.BodyCare["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <h5>Health</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="CommonAilments"
                        label="Explain common ailments"
                        checked={data.CommonAilments["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="Thermometer"
                        label="Demonstrate proper use of a thermometer or thermometer strip"
                        checked={data.Thermometer["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="Puberty"
                        label="Explain puberty"
                        checked={data.Puberty["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="SafeSex"
                        label="Explain how to prevent pregnancy and STD's through safe sex practices"
                        checked={data.SafeSex["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <h5>Self-Image</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="PositiveSelfImage"
                        label="Explain the importance of a positive self-concept"
                        checked={data.PositiveSelfImage["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="WeatherClothes"
                        label="Demonstrate proper attire for weather"
                        checked={data.WeatherClothes["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="SocialRelationships"
                expandStatus={expandStatus}
                className="mt-2 ms-3"
                title="Social Relationships"
              >
                <h5>Personal Development</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="PositiveAttitude"
                        label="Explain positive attitudes"
                        checked={data.PositiveAttitude["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="InterpersonalRelationships"
                        label="Explain importance of interpersonal relationships"
                        checked={
                          data.InterpersonalRelationships["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="Respect"
                        label="Demonstrate how to be polite/demonstrate respect"
                        checked={data.Respect["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="Etiquette"
                        label="Demonstrate appropriate social etiquette"
                        checked={data.Etiquette["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="Communication"
                        label="Explain and practice 2 types of communication"
                        checked={data.Communication["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="DecisionMaking"
                        label="Practice decision making skills"
                        checked={data.DecisionMaking["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="ProblemsAndFeelings"
                        label="Discuss relationship of problems and feelings"
                        checked={data.ProblemsAndFeelings["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="SelfControl"
                        label="Practice self control"
                        checked={data.SelfControl["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="FriendChoices"
                        label="Discuss choosing friends and how to evaluate friendshipsoLeisure"
                        checked={data.FriendChoices["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <h5>Leisure Time</h5>
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="HomeEntertainment"
                        label="Explore home entertainment opportunities"
                        checked={data.HomeEntertainment["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="OutsideActivities"
                        label="Explore outside activity opportunities (such as sports, organizations, clubs, community events)"
                        checked={data.OutsideActivities["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="MoneyManagement"
                expandStatus={expandStatus}
                className="mt-2 ms-3"
                title="Money Management"
              >
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="SavingMoney"
                        label="Explain the importance of saving"
                        checked={data.SavingMoney["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="SavingOrSpending"
                        label="Discuss saving vs. spending"
                        checked={data.SavingOrSpending["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="TotalCost"
                        label="Determine how to figure the total cost of purchase"
                        checked={data.TotalCost["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="Education"
                expandStatus={expandStatus}
                className="mt-2 ms-3"
                title="Education"
              >
                <div className="ms-3">
                  <div className="row ms-1">
                    <div className="col-4">
                      <Checkbox
                        name="Attendance"
                        label="Discuss the importance of attendance and punctuality"
                        checked={data.Attendance["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-4">
                      <Checkbox
                        name="Truancy"
                        label="Discuss and explain truancy"
                        checked={data.Truancy["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <Checkbox
                name="AllBasicSkills"
                label="Youth has completed all of the skills above"
                checked={data.AllBasicSkills["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              {data.AllBasicSkills["@value"] === "true" && (
                <>
                  <div className="text-center">
                    <h4>Advanced Independent Living Skills</h4>
                  </div>
                  <ExpandDiv
                    id="AdvancedDailyLivingSkills"
                    expandStatus={expandStatus}
                    className="mt-2 ms-3"
                    title="Daily Living"
                  >
                    <h5>Telephone Skills</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedTexting"
                            label="Demonstrate texting or media messaging using a cell phone"
                            checked={data.AdvancedTexting["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedCellPlans"
                            label="Compare and contrast cell phone plans"
                            checked={
                              data.AdvancedCellPlans["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Internet</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedEmail"
                            label="Demonstrate use of email"
                            checked={data.AdvancedEmail["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedSocialNetworking"
                            label="Explain social networking/dangers of social networking"
                            checked={
                              data.AdvancedSocialNetworking["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedDownloading"
                            label="Explain dangers of downloading (i.e. viruses)"
                            checked={
                              data.AdvancedDownloading["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedJobSearch"
                            label="Demonstrate how to complete a job search online"
                            checked={
                              data.AdvancedJobSearch["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Transportation</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedTransportation"
                            label="Identify public transportation services"
                            checked={
                              data.AdvancedTransportation["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedTaxis"
                            label="Explain taxis"
                            checked={data.AdvancedTaxis["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedSchedules"
                            label="Demonstrate how to read public transport schedules (routes)"
                            checked={
                              data.AdvancedSchedules["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Nutrition/Cooking</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedFastFood"
                            label="Compare and contrast fast food vs home cooking"
                            checked={data.AdvancedFastFood["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedMealPlanning"
                            label="Demonstrate menu planning for a meal"
                            checked={
                              data.AdvancedMealPlanning["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedGroceries"
                            label="Develop a grocery list"
                            checked={
                              data.AdvancedGroceries["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedCooking"
                            label="Demonstrate knowledge of cooking utensils (pots, pans, etc)"
                            checked={data.AdvancedCooking["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedIngredients"
                            label="Demonstrate understanding ingredient measurements"
                            checked={
                              data.AdvancedIngredients["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedCookingMethods"
                            label="Explain various ways to cook items"
                            checked={
                              data.AdvancedCookingMethods["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedMealPrep"
                            label="Discuss importance of management of meal preparation"
                            checked={data.AdvancedMealPrep["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedBreakfast"
                            label="Plan and prepare a breakfast"
                            checked={
                              data.AdvancedBreakfast["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedLunch"
                            label="Plan and prepare a lunch"
                            checked={data.AdvancedLunch["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedDinner"
                            label="Plan and prepare a dinner"
                            checked={data.AdvancedDinner["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedTakeOut"
                            label="Plan and prepare a meal to take out of the home"
                            checked={data.AdvancedTakeOut["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedSetTable"
                            label="Demonstrate how to set a table"
                            checked={data.AdvancedSetTable["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedServeFood"
                            label="Demonstrate serving food"
                            checked={
                              data.AdvancedServeFood["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedEtiquette"
                            label="Demonstrate appropriate table etiquette"
                            checked={
                              data.AdvancedEtiquette["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedEntertainGuests"
                            label="Discuss entertaining guests for dinner"
                            checked={
                              data.AdvancedEntertainGuests["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Home Maintenance</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedCleaningSafety"
                            label="Demonstrate safety with cleaning supplies"
                            checked={
                              data.AdvancedCleaningSafety["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedFloorCare"
                            label="Demonstrate floor care (sweeping, mopping)"
                            checked={
                              data.AdvancedFloorCare["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedLaundry"
                            label="Demonstrate correct laundry procedures"
                            checked={data.AdvancedLaundry["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedClothingRepair"
                            label="Explain how to perform minor clothing repair"
                            checked={
                              data.AdvancedClothingRepair["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedHomeRepair"
                            label="Explain safety practices with home repair"
                            checked={
                              data.AdvancedHomeRepair["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedYardSafety"
                            label="Discuss proper use, care, safety with yard tools"
                            checked={
                              data.AdvancedYardSafety["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedYardMaintenance"
                            label="Discuss yard maintenance (raking, trimming bushes, cutting the grass)"
                            checked={
                              data.AdvancedYardMaintenance["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </ExpandDiv>
                  <ExpandDiv
                    id="AdvancedSelfCare"
                    expandStatus={expandStatus}
                    className="mt-2 ms-3"
                    title="Self-Care"
                  >
                    <h5>Hygiene</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedShaving"
                            label="Demonstrate shaving safety and techniques"
                            checked={data.AdvancedShaving["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Health</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedFirstAid"
                            label="Explain knowledge of first aid"
                            checked={data.AdvancedFirstAid["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedMedications"
                            label="Explain knowledge of medications and dosages"
                            checked={
                              data.AdvancedMedications["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedExerciseImportance"
                            label="Importance of a regular exercise routine"
                            checked={
                              data.AdvancedExerciseImportance["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedExerciseRoutine"
                            label="How to establish a regular exercise routine"
                            checked={
                              data.AdvancedExerciseRoutine["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Self-Image</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedPositiveSelfImage"
                            label="Discuss importance of positive self concept"
                            checked={
                              data.AdvancedPositiveSelfImage["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedExerciseImage"
                            label="Discuss how exercising can create a positive self image and positive stress management"
                            checked={
                              data.AdvancedExerciseImage["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </ExpandDiv>
                  <ExpandDiv
                    id="AdvancedSocialRelationships"
                    expandStatus={expandStatus}
                    className="mt-2 ms-3"
                    title="Social Relationships"
                  >
                    <h5>Personal Development</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedInteractions"
                            label="Discuss appropriate interactions with the opposite sex"
                            checked={
                              data.AdvancedInteractions["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Leisure Time</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedResources"
                            label="Recognize local community resources"
                            checked={
                              data.AdvancedResources["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedEntertainFriends"
                            label="Plan for entertaining friends at home"
                            checked={
                              data.AdvancedEntertainFriends["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedVolunteer"
                            label="Discuss volunteer opportunities"
                            checked={
                              data.AdvancedVolunteer["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </ExpandDiv>
                  <ExpandDiv
                    id="AdvancedMoneyManagement"
                    expandStatus={expandStatus}
                    className="mt-2 ms-3"
                    title="Money Management"
                  >
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedBudget"
                            label="Plan a monthly and long range budget"
                            checked={data.AdvancedBudget["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedBankAccount"
                            label="Explain how to open a checking and savings account"
                            checked={
                              data.AdvancedBankAccount["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedMoneyOrder"
                            label="Complete a money order"
                            checked={
                              data.AdvancedMoneyOrder["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedCredit"
                            label="Explain advantages and disadvantages of using credit"
                            checked={data.AdvancedCredit["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedConsumerProblems"
                            label="Explain how to get help with consumer problems"
                            checked={
                              data.AdvancedConsumerProblems["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedBills"
                            label="Identify various methods of paying bills"
                            checked={data.AdvancedBills["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedReceipts"
                            label="Explain importance of saving receipts and stubs"
                            checked={data.AdvancedReceipts["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedBalancing"
                            label="Maintaining accurate record of balances"
                            checked={
                              data.AdvancedBalancing["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </ExpandDiv>
                  <ExpandDiv
                    id="AdvancedHousing"
                    expandStatus={expandStatus}
                    className="mt-2 ms-3"
                    title="Housing"
                  >
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedPrivacy"
                            label="Discuss importance of personal privacy"
                            checked={data.AdvancedPrivacy["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedNeeds"
                            label="Discuss housing appropriate to personal needs / income"
                            checked={data.AdvancedNeeds["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <Checkbox
                          name="AdvancedRoommate"
                          label="Discuss choosing a roommate"
                          checked={data.AdvancedRoommate["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedRoommatePrivacy"
                            label="Explain respecting roommate’s privacy"
                            checked={
                              data.AdvancedRoommatePrivacy["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedFriendBreakup"
                            label="Discuss “parting as friends”"
                            checked={
                              data.AdvancedFriendBreakup["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedMoving"
                            label="Discuss moving options"
                            checked={data.AdvancedMoving["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedPacking"
                            label="Explain proper packing skills / arrangements"
                            checked={data.AdvancedPacking["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedHousingCosts"
                            label="Explain Itemize “start-up” housing costs"
                            checked={
                              data.AdvancedHousingCosts["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedLease"
                            label="Explain how to read a lease / rental agreement"
                            checked={data.AdvancedLease["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedRights"
                            label="Discusslandlord / renter rights and responsibilities"
                            checked={data.AdvancedRights["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </ExpandDiv>
                  <ExpandDiv
                    id="AdvancedCareerPlanning"
                    expandStatus={expandStatus}
                    className="mt-2 ms-3"
                    title="Career Planning"
                  >
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedResume"
                            label="Explain the components of a resume"
                            checked={data.AdvancedResume["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedCreateResume"
                            label="Create a resume"
                            checked={
                              data.AdvancedCreateResume["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedJobApplication"
                            label="Demonstrate how to fill out a job application"
                            checked={
                              data.AdvancedJobApplication["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedJobDress"
                            label="Discuss the appropriate dress for different job interviews and jobs"
                            checked={data.AdvancedJobDress["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedInterview"
                            label="Demonstrate basic courtesy during an interview"
                            checked={
                              data.AdvancedInterview["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedWorkPunctuality"
                            label="Explain theimportance of punctuality for work"
                            checked={
                              data.AdvancedWorkPunctuality["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedTime"
                            label="Practice positive time management"
                            checked={data.AdvancedTime["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedDirections"
                            label="Demonstrate an ability to follow directions from a supervisor"
                            checked={
                              data.AdvancedDirections["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedVolunteerNetwork"
                            label="Discuss how volunteering can be a great source for networking"
                            checked={
                              data.AdvancedVolunteerNetwork["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedNetworkImportance"
                            label="Discuss the importance and relevance of networking"
                            checked={
                              data.AdvancedNetworkImportance["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </ExpandDiv>
                  <ExpandDiv
                    id="AdvancedFuturePlanning"
                    expandStatus={expandStatus}
                    className="mt-2 ms-3"
                    title="FuturePlanning"
                  >
                    <h5>Documentation</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedIDCard"
                            label="Explain how to Obtainan ID card"
                            checked={data.AdvancedIDCard["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedSocialCard"
                            label="Explain where to get a Social security card, birth certificate, etc"
                            checked={
                              data.AdvancedSocialCard["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedSelectiveService"
                            label="Explain how to registerfor the Selective Service"
                            checked={
                              data.AdvancedSelectiveService["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedPersonalDocs"
                            label="Discuss the importance of maintaining copies of personal documentation"
                            checked={
                              data.AdvancedPersonalDocs["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Community</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedSEO"
                            label="Identify services available from State Employment Office"
                            checked={data.AdvancedSEO["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedHD"
                            label="Identify services available from Health Department"
                            checked={data.AdvancedHD["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedPO"
                            label="Identify services available from Post Office"
                            checked={data.AdvancedPO["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedPL"
                            label="Identify services available from Public Library"
                            checked={data.AdvancedPL["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedED"
                            label="Identify services available from Emergency Departments"
                            checked={data.AdvancedED["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedPAO"
                            label="Identify services available from Public Assistance Office"
                            checked={data.AdvancedPAO["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Education</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedSchools"
                            label="Identify schools, colleges, vocational training option available in the community"
                            checked={data.AdvancedSchools["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedSATACT"
                            label="Explain the SAT/ACT"
                            checked={data.AdvancedSATACT["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedGraduateSchool"
                            label="Discuss applying to post secondary education"
                            checked={
                              data.AdvancedGraduateSchool["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedFAFSA"
                            label="Explain the FAFSA"
                            checked={data.AdvancedFAFSA["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedTuitionAssistance"
                            label="Explain tuition assistance "
                            checked={
                              data.AdvancedTuitionAssistance["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          {" "}
                          <Checkbox
                            name="AdvancedAttendance"
                            label="Discuss the importance of attendance and punctuality"
                            checked={
                              data.AdvancedAttendance["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedTruancy"
                            label="Discuss and explain truancy"
                            checked={data.AdvancedTruancy["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Health</h5>
                    <div className="ms-3">
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedHealthcare"
                            label="Discuss options for health care (urgent care centers, health clinics)"
                            checked={
                              data.AdvancedHealthcare["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedDoctors"
                            label="Discuss choosing a Doctor, Dentist, etc"
                            checked={data.AdvancedDoctors["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedMedicalExams"
                            label="Discuss annual medical exams"
                            checked={
                              data.AdvancedMedicalExams["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col-4">
                          <Checkbox
                            name="AdvancedInsurance"
                            label="Discuss options for health insurance"
                            checked={
                              data.AdvancedInsurance["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </ExpandDiv>
                </>
              )}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default KYILSkillsChecklist;
